import { motion } from "framer-motion";
import { ChatCircleDots, PaperPlaneRight } from "@phosphor-icons/react";
import { computeWidgetStyles } from "@/lib/widgetPresets";

/** Live, instantly-updating mock of the embedded chat widget on a faux website. */
export default function WidgetPreview({ cfg }) {
  const s = computeWidgetStyles(cfg);
  const dark = s.theme === "dark";
  const panelBg = dark ? "#0f1117" : "#ffffff";
  const panelText = dark ? "#e5e7eb" : "#18181b";
  const botBubble = dark ? "#1f2430" : "#f1f2f6";
  const subText = dark ? "#9aa3b2" : "#71717a";

  // scale the real panel size down to fit the preview box
  const scale = 0.62;
  const pw = Math.round(s.panelW * scale);
  const ph = Math.round(Math.min(s.panelH, 470) * scale);
  const align = s.side === "left" ? "items-start" : "items-end";
  const launcherInner = s.isPill
    ? <span className="flex items-center gap-1.5 px-3.5" style={{ height: s.launcher * 0.74 }}>
        <ChatCircleDots size={s.icon * 0.8} weight="fill" />
        <span className="text-[12px] font-semibold whitespace-nowrap">{s.label || "Chatta"}</span>
      </span>
    : <span className="flex items-center justify-center" style={{ width: s.launcher * 0.82, height: s.launcher * 0.82 }}>
        <ChatCircleDots size={s.icon} weight="fill" />
      </span>;

  return (
    <div className="rounded-xl overflow-hidden border border-zinc-200" data-testid="widget-preview">
      {/* faux browser bar */}
      <div className="h-8 bg-zinc-100 border-b border-zinc-200 flex items-center px-3 gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        <span className="ml-3 text-[11px] text-zinc-400 font-mono truncate">din-sajt.se</span>
      </div>
      {/* faux page + widget */}
      <div className="relative bg-gradient-to-br from-zinc-50 to-zinc-100 h-[360px] p-4 overflow-hidden">
        <div className="space-y-2 opacity-60">
          <div className="h-3 w-28 bg-zinc-300 rounded" />
          <div className="h-2 w-48 bg-zinc-200 rounded" />
          <div className="h-2 w-40 bg-zinc-200 rounded" />
          <div className="h-20 w-full bg-zinc-200/70 rounded-lg mt-3" />
        </div>

        <div className={`absolute bottom-0 flex flex-col gap-2.5 ${align}`}
          style={{ [s.side]: s.offset, bottom: s.offset }}>
          {/* open panel */}
          <div style={{ width: pw, height: ph, borderRadius: s.panelRadius, background: panelBg, color: panelText,
            boxShadow: "0 16px 40px rgba(0,0,0,.22)", overflow: "hidden", border: dark ? "1px solid #232734" : "1px solid #ececed" }}>
            <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: s.color }}>
              <div className="h-6 w-6 rounded-full bg-white/25 flex items-center justify-center text-white text-[11px] font-bold">
                {(cfg?.name || "A").slice(0, 1).toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="text-[11px] font-bold text-white">{cfg?.name || "Senzum-agent"}</div>
                <div className="text-[9px] text-white/80">Online</div>
              </div>
            </div>
            <div className="p-2.5 space-y-2">
              <div className="text-[10px] px-2.5 py-1.5 max-w-[80%]"
                style={{ background: botBubble, color: panelText, borderRadius: s.bubbleRadius }}>
                {cfg?.welcome_message || "Hej! Hur kan jag hjälpa dig?"}
              </div>
              <div className="text-[10px] px-2.5 py-1.5 max-w-[70%] ml-auto text-white"
                style={{ background: s.color, borderRadius: s.bubbleRadius }}>
                Jag har en fråga om min order.
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 px-2.5 pb-2.5" style={{ width: pw }}>
              <div className="flex items-center gap-1.5 border rounded-full px-2.5 py-1"
                style={{ borderColor: dark ? "#2a2f3c" : "#e4e4e7" }}>
                <span className="text-[10px] flex-1" style={{ color: subText }}>Skriv ett meddelande…</span>
                <PaperPlaneRight size={12} weight="fill" style={{ color: s.color }} />
              </div>
            </div>
          </div>

          {/* launcher */}
          <motion.div
            className={`text-white shadow-lg flex items-center justify-center ${s.side === "left" ? "self-start" : "self-end"}`}
            style={{ background: s.color, borderRadius: s.launcherRadius, minWidth: s.launcher, height: s.launcher, boxShadow: "0 8px 22px rgba(0,0,0,.25)" }}
            animate={s.bounce ? { y: [0, -7, 0] } : {}}
            transition={s.bounce ? { duration: 1.4, repeat: Infinity, ease: "easeInOut" } : {}}
          >
            {launcherInner}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
